// acquisition.ts — CORE-1137
//
// Сквозная marketing attribution: откуда человек пришёл (реклама, партнёр,
// органика) — и как это доживает до его регистрации и дальнейших действий.
//
// ── Почему это отдельный контур, а не расширение referral ───────────────────
// Referral (CORE-956/965) отвечает на вопрос «кто из НАШИХ привёл этого
// человека», у него есть выплаты, налоги и идентичность пригласившего. Здесь
// вопрос другой — «какая кампания/креатив привели трафик», участников с
// деньгами нет, а объём на порядки больше. Переиспользовать referral-модель
// как единственный механизм — значит завести рекламному трафику фиктивных
// «пригласивших» и смешать две воронки, которые считаются по-разному.
// Поэтому: отдельный словарь событий поверх того же `AnalyticsEvents`
// (как homeAnalytics/shareAnalytics/gamificationAnalytics), без новой трубы.
//
// ── Контракт first-touch / last-touch ───────────────────────────────────────
// Ровно один раз зафиксирован здесь, чтобы отчёты не трактовали источник
// по-разному:
//
//   first-touch — САМЫЙ ПЕРВЫЙ визит устройства, каким бы он ни был, включая
//     заход вообще без меток. Пишется один раз и не переписывается никогда.
//     Если бы мы записывали первый визит только «с метками», первым известным
//     источником оказалась бы вторая по счёту кампания — та, которой повезло
//     принести UTM, — и органика перестала бы существовать в отчёте.
//
//   last-touch — последнее ПРИПИСЫВАЕМОЕ касание: визит, принёсший хоть какой-то
//     сигнал (любая utm_*, рекламный click-id, внешний реферер, `?ref=share`).
//     Повторный прямой заход last-touch НЕ переписывает: иначе атрибуция любой
//     кампании выцветала бы в «direct» на второй день, и отчёт показывал бы,
//     что платный трафик не приносит никого.
//
// `isFirstTouch` в событии отделяет «этот визит и есть первый» от «первый
// известен, вот он».
//
// ── Приватность ─────────────────────────────────────────────────────────────
// Из адреса берётся ТОЛЬКО whitelist параметров (см. `TRACKED_PARAMS`), а не
// произвольная query string: рекламные и почтовые рассылки регулярно тащат в
// адресе email/телефон получателя, и «сохраним всё, разберёмся потом» — это
// прямая утечка PII в аналитическую таблицу, которую читают админы и выгружают
// наружу. Реферер сокращается до хоста по той же причине (полный URL несёт
// чужую query string). Значения дополнительно просеиваются `scrubValue`.
//
// Зеркало имён событий — `AnalyticsEventNames.Acquisition` в
// `Agape.Shared/Models/AnalyticsEventNames.cs`. Держим списки в lock-step:
// переименование с одной стороны молча опустошает отчёт, ничего не ломая.

import { Platform } from 'react-native';
import { analytics } from './analytics';
import { storage } from '../utils/storage';

// ── Словарь событий ─────────────────────────────────────────────────────────

export const AcquisitionEvents = {
  /**
   * Одна строка на аналитическую сессию: с чем человек в неё вошёл. Несёт и
   * first-, и last-touch целиком, поэтому отчёту не нужно копировать UTM в
   * каждое продуктовое событие — связь делается серверно по `sessionId`.
   *
   * Повторяется внутри живой сессии только если пришло НОВОЕ приписываемое
   * касание (человек вернулся по другому объявлению, не дождавшись конца
   * сессии) — иначе строка так и осталась бы с прежней кампанией.
   */
  touch: 'acquisition_touch',
  /**
   * Анонимная сессия стала пользователем. Бэкенд проставляет `UserId` из JWT,
   * а `sessionId` тот же, что у `acquisition_touch` — это и есть шов
   * «анонимный визит → аккаунт».
   */
  identified: 'acquisition_identified',
} as const;

export type AcquisitionEventName = (typeof AcquisitionEvents)[keyof typeof AcquisitionEvents];

// ── Что берём из адреса ─────────────────────────────────────────────────────

/** Классические UTM. Ключ — параметр адреса, значение — имя поля контекста. */
const UTM_PARAMS: Record<string, keyof AcquisitionTouch> = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_content: 'content',
  utm_term: 'term',
};

/**
 * Рекламные click-id: единственный способ сшить визит с кабинетом площадки,
 * когда UTM потерялись по дороге (редирект, сокращалка, ретаргетинг). Значение
 * непрозрачно и площадкой же выдано — PII в нём нет.
 *
 * Ключ — параметр, значение — площадка, которую он обозначает: хранить «какой
 * это был click-id» полезнее, чем сам идентификатор, а сам идентификатор нужен
 * для сверки с кабинетом.
 */
const CLICK_ID_PARAMS: Record<string, string> = {
  gclid: 'google',
  gbraid: 'google',
  wbraid: 'google',
  msclkid: 'microsoft',
  fbclid: 'meta',
  igshid: 'meta',
  ttclid: 'tiktok',
  twclid: 'x',
  li_fat_id: 'linkedin',
  yclid: 'yandex',
  ymclid: 'yandex',
  rdt_cid: 'reddit',
  epik: 'pinterest',
};

/** Полный whitelist — всё, что вообще читается из query string. */
export const TRACKED_PARAMS: readonly string[] = [
  ...Object.keys(UTM_PARAMS),
  ...Object.keys(CLICK_ID_PARAMS),
];

/**
 * `?ref=share` ставит `buildShareUrl` (CORE-788). Для acquisition это законный
 * источник — «пришёл по ссылке, которой поделились», — и без него такой визит
 * записался бы как `direct`, то есть органика съела бы вирусный канал.
 *
 * Считаем его только при точном значении `share`. Любое другое `ref=` не
 * трогаем вовсе: реферальные коды — контур CORE-956/965, и приписывать им
 * рекламный source значило бы смешать две воронки.
 */
const SHARE_REF_PARAM = 'ref';
const SHARE_REF_VALUE = 'share';

const MAX_VALUE_LENGTH = 100;
const MAX_PATH_LENGTH = 200;
const MAX_HOST_LENGTH = 128;

// ── Модель ──────────────────────────────────────────────────────────────────

/** Одно касание: нормализованный контекст прихода. */
export interface AcquisitionTouch {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  clickId?: string;
  clickIdKind?: string;
  landingPath?: string;
  referrerHost?: string;
  /** ISO-8601 UTC. */
  touchAtUtc: string;
}

/** Что лежит в сторадже устройства между визитами. */
export interface AcquisitionState {
  /** Стабильный анонимный идентификатор устройства. Случайный, не PII. */
  anonId: string;
  first: AcquisitionTouch;
  last: AcquisitionTouch;
  /** Сколько приписываемых касаний уже было. `1` — только первое. */
  touchCount: number;
}

export const ACQUISITION_STORAGE_KEY = 'agape_acquisition_v1';

// ── Разбор и нормализация ───────────────────────────────────────────────────

/**
 * Отбраковать значение, похожее на PII, и обрезать слишком длинное.
 *
 * Возвращает `undefined` вместо «почищенной» строки намеренно: наполовину
 * вычищенный email в аналитике хуже отсутствующего поля — он выглядит как
 * настоящая кампания и попадает в разрезы.
 */
export function scrubValue(raw: string | null | undefined): string | undefined {
  if (raw == null) return undefined;
  let value = String(raw).trim();
  if (!value) return undefined;

  // `%40` доезжает нераскодированным чаще, чем кажется: рассылки собирают
  // адрес конкатенацией и кодируют его дважды.
  const decoded = value.replace(/%40/gi, '@');
  if (decoded.includes('@')) return undefined;
  if (looksLikePhone(value)) return undefined;

  if (value.length > MAX_VALUE_LENGTH) value = value.slice(0, MAX_VALUE_LENGTH);
  return value;
}

/**
 * Похоже ли значение на телефонный номер, который мог утечь из mail-merge.
 *
 * Раньше правилом было «10+ цифр подряд → телефон», и оно рубило легальные имена
 * кампаний с таймстампом или id рекламной площадки (`qa1137_1788116382098`,
 * `20260831_brandlift`) — QA поймала на этом потерю обязательного `utm_campaign`
 * (CORE-1137). Числовой id и телефон по одному только количеству цифр не
 * различить, поэтому требуем именно телефонные ПРИЗНАКИ, а не длину:
 *
 *   • значение состоит ТОЛЬКО из цифр и телефонных разделителей (`+ () - . ` и
 *     пробел) — у имени кампании есть буквы/подчёркивания, у телефона их нет;
 *   • в нём 10–15 значащих цифр (диапазон номера по ITU E.164);
 *   • и есть ведущий `+` ЛИБО разделитель между группами цифр.
 *
 * Так `+7 (999) 123-45-67` и `8 999 123-45-67` отбраковываются, а голая цепочка
 * цифр (таймстамп кампании) проходит: приписать ей телефон — значит вернуть тот
 * самый дефект, ради которого правило сужается.
 */
function looksLikePhone(value: string): boolean {
  if (!/^\+?[\d\s().-]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15) return false;
  return value.trimStart().startsWith('+') || /\d[\s().-]+\d/.test(value);
}

/** Разложить query string в пары. Без `URLSearchParams` — его нет на части RN. */
export function parseQuery(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  const body = (search || '').replace(/^\?/, '');
  if (!body) return out;
  for (const chunk of body.split('&')) {
    if (!chunk) continue;
    const eq = chunk.indexOf('=');
    const rawKey = eq === -1 ? chunk : chunk.slice(0, eq);
    const rawVal = eq === -1 ? '' : chunk.slice(eq + 1);
    let key: string;
    let val: string;
    try {
      key = decodeURIComponent(rawKey.replace(/\+/g, ' ')).trim().toLowerCase();
      val = decodeURIComponent(rawVal.replace(/\+/g, ' '));
    } catch {
      // Битая %-последовательность: параметр целиком пропускаем, а не
      // сохраняем сырьё — сырьё и есть тот самый неразобранный query string.
      continue;
    }
    if (key && !(key in out)) out[key] = val;
  }
  return out;
}

/** Хост из полного URL реферера. Пустая строка → `undefined`. */
export function referrerHost(referrer: string | null | undefined): string | undefined {
  if (!referrer) return undefined;
  const match = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i.exec(referrer.trim());
  if (!match) return undefined;
  const host = match[1].replace(/^[^@]*@/, '').replace(/:\d+$/, '').toLowerCase();
  if (!host || host.length > MAX_HOST_LENGTH) return undefined;
  return host;
}

/** Путь без query и hash, обрезанный по длине. */
export function normalizePath(pathname: string | null | undefined): string | undefined {
  if (!pathname) return undefined;
  const path = String(pathname).split('#')[0].split('?')[0].trim();
  if (!path) return undefined;
  return path.length > MAX_PATH_LENGTH ? path.slice(0, MAX_PATH_LENGTH) : path;
}

/** Сырьё одного визита — то, что умеет дать платформа. */
export interface VisitInput {
  /** Query string визита (`?utm_source=…` или без `?`). */
  search?: string | null;
  /** Путь приземления. */
  pathname?: string | null;
  /** Полный URL реферера (web) — сократится до хоста. */
  referrer?: string | null;
  /** Собственный хост, чтобы отличить внутренний переход от внешнего прихода. */
  selfHost?: string | null;
  /** ISO-8601 UTC. Передаётся явно — тесты не должны зависеть от часов. */
  nowUtc: string;
}

/**
 * Собрать касание из визита.
 *
 * Возвращает `attributed: false`, если сигнала прихода нет вовсе — ни меток, ни
 * click-id, ни внешнего реферера. Такое касание годится в first-touch (первый
 * визит бывает и прямым), но last-touch не переписывает.
 */
export function readVisit(input: VisitInput): { touch: AcquisitionTouch; attributed: boolean } {
  const params = parseQuery(input.search || '');
  const touch: AcquisitionTouch = { touchAtUtc: input.nowUtc };

  for (const [param, field] of Object.entries(UTM_PARAMS)) {
    const value = scrubValue(params[param]);
    if (!value) continue;
    // source/medium — измерения с маленьким словарём, регистр в них только
    // плодит дубли строк в отчёте («Google» и «google» разными колонками).
    // campaign/content/term человек читает глазами — там регистр сохраняем.
    (touch as unknown as Record<string, unknown>)[field] =
      field === 'source' || field === 'medium' ? value.toLowerCase() : value;
  }

  for (const [param, kind] of Object.entries(CLICK_ID_PARAMS)) {
    const value = scrubValue(params[param]);
    if (!value) continue;
    touch.clickId = value;
    touch.clickIdKind = kind;
    break;
  }

  const host = referrerHost(input.referrer);
  const self = (input.selfHost || '').toLowerCase();
  // Внутренний переход — не приход. Иначе каждая навигация по SPA после
  // перезагрузки выглядела бы как новое касание с реферером «мы сами».
  const external = host && host !== self ? host : undefined;
  if (external) touch.referrerHost = external;

  const path = normalizePath(input.pathname);
  if (path) touch.landingPath = path;

  const hasUtm = Boolean(touch.source || touch.medium || touch.campaign || touch.content || touch.term);
  const shared = params[SHARE_REF_PARAM] === SHARE_REF_VALUE;

  if (!hasUtm && shared) {
    // Пришёл по ссылке, которой поделились. Источник наш, канал — вирусный;
    // реферальный контур (CORE-956/965) при этом не задет ничем.
    touch.source = 'agapella';
    touch.medium = 'share';
  } else if (!hasUtm && external) {
    // Внешний реферер без меток — это органический партнёрский трафик, и
    // называть его «direct» значит потерять единственное, что о нём известно.
    touch.source = external;
    touch.medium = 'referral';
  }

  const attributed = hasUtm || shared || Boolean(touch.clickId) || Boolean(external);
  if (!attributed) {
    touch.source = 'direct';
    touch.medium = 'none';
  }

  return { touch, attributed };
}

// ── Правила first/last-touch ────────────────────────────────────────────────

/**
 * Одно ли это касание по содержанию. Время и путь приземления не считаются:
 * перезагрузка страницы с теми же метками — тот же самый приход, а не второй.
 */
function sameTouch(a: AcquisitionTouch, b: AcquisitionTouch): boolean {
  return a.source === b.source
    && a.medium === b.medium
    && a.campaign === b.campaign
    && a.content === b.content
    && a.term === b.term
    && a.clickId === b.clickId
    && a.referrerHost === b.referrerHost;
}

/**
 * Свести сохранённое состояние с новым визитом.
 *
 * Два флага, а не один, потому что «сохранить» и «сообщить» — разные события.
 * Перезагрузка страницы, на адресе которой всё ещё висят UTM, обновляет время
 * последнего касания (`persist`), но новым касанием не является (`emit: false`).
 * Один флаг на оба смысла означал бы либо строку в отчёте на каждую
 * перезагрузку рекламной посадочной, либо навсегда застывшее время.
 */
export function applyVisit(
  previous: AcquisitionState | null,
  visit: { touch: AcquisitionTouch; attributed: boolean },
  anonId: string,
): { state: AcquisitionState; persist: boolean; emit: boolean; isFirstTouch: boolean } {
  if (!previous) {
    return {
      state: { anonId, first: visit.touch, last: visit.touch, touchCount: 1 },
      persist: true,
      emit: true,
      isFirstTouch: true,
    };
  }

  // Первое касание неприкосновенно: «повторный заход без UTM не должен стирать
  // уже известный first-touch» — и заход С UTM тоже не должен, иначе first-touch
  // означал бы то же самое, что last-touch.
  if (!visit.attributed) {
    return { state: previous, persist: false, emit: false, isFirstTouch: false };
  }

  if (sameTouch(previous.last, visit.touch)) {
    return {
      state: { ...previous, last: { ...previous.last, touchAtUtc: visit.touch.touchAtUtc } },
      persist: true,
      emit: false,
      isFirstTouch: false,
    };
  }

  return {
    state: {
      ...previous,
      anonId: previous.anonId || anonId,
      last: visit.touch,
      touchCount: previous.touchCount + 1,
    },
    persist: true,
    emit: true,
    isFirstTouch: false,
  };
}

// ── Форма свойств события ───────────────────────────────────────────────────

/**
 * Плоские свойства для `AnalyticsEvents.Properties`.
 *
 * Плоско, а не вложенно, потому что читать это будут в SQL:
 * `properties->>'campaign'` — last-touch, `properties->>'firstCampaign'` —
 * first-touch. Вложенность превратила бы каждый разрез в лишний уровень
 * разыменования, а разрезы здесь и есть смысл таблицы.
 */
export function touchProperties(state: AcquisitionState, isFirstTouch: boolean): Record<string, unknown> {
  const props: Record<string, unknown> = {
    anonId: state.anonId,
    isFirstTouch,
    touchCount: state.touchCount,
    touchAtUtc: state.last.touchAtUtc,
    firstTouchAtUtc: state.first.touchAtUtc,
  };

  const copy = (touch: AcquisitionTouch, prefix: '' | 'first') => {
    const put = (key: string, value?: string) => {
      if (!value) return;
      props[prefix ? `${prefix}${key[0].toUpperCase()}${key.slice(1)}` : key] = value;
    };
    put('source', touch.source);
    put('medium', touch.medium);
    put('campaign', touch.campaign);
    put('content', touch.content);
    put('term', touch.term);
    put('clickId', touch.clickId);
    put('clickIdKind', touch.clickIdKind);
    put('landingPath', touch.landingPath);
    put('referrerHost', touch.referrerHost);
  };

  copy(state.last, '');
  copy(state.first, 'first');
  return props;
}

/** Способ, которым сессия стала пользователем. Замкнутый словарь. */
export type IdentifyMethod = 'password' | 'google' | 'apple' | 'passkey' | 'register';

// ── Сторадж ─────────────────────────────────────────────────────────────────

function randomId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 16; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
  return `${Date.now().toString(36)}-${out}`;
}

export async function readState(): Promise<AcquisitionState | null> {
  const raw = await storage.getJSON<AcquisitionState>(ACQUISITION_STORAGE_KEY);
  if (!raw || typeof raw !== 'object') return null;
  if (!raw.anonId || !raw.first?.touchAtUtc || !raw.last?.touchAtUtc) return null;
  return raw;
}

async function writeState(state: AcquisitionState): Promise<void> {
  try {
    await storage.setJSON(ACQUISITION_STORAGE_KEY, state);
  } catch {
    // Приватный режим / переполненный сторадж. Атрибуция этого визита уже
    // ушла событием — терять её нечем, а падать здесь незачем.
  }
}

// ── Снимок адреса на момент загрузки ────────────────────────────────────────

/**
 * Адрес читается ОДИН РАЗ при импорте модуля, а не в эффекте.
 *
 * React Navigation переписывает адрес вкладки на своей первой же навигации
 * (незнакомый путь превращается в «/», псевдонимы в `pathAliases` — в
 * канонический путь), и вместе с путём уезжает query string. Эффект,
 * дождавшийся авторизации, читал бы уже переписанный адрес — то есть терял бы
 * ровно те метки, ради которых всё это и делается.
 */
const webEntry = (() => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const loc = window.location;
  return {
    search: loc?.search || '',
    pathname: loc?.pathname || '/',
    referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
    selfHost: loc?.hostname || '',
  };
})();

/** Снимок входного адреса вкладки. Экспортирован для хука и тестов. */
export function webEntrySnapshot() {
  return webEntry;
}

// ── Эмит ────────────────────────────────────────────────────────────────────

/**
 * Учесть визит: свести с сохранённым состоянием и, если что-то изменилось,
 * записать строку `acquisition_touch`.
 *
 * `force` шлёт строку даже когда касание ничего не изменило — так открывается
 * НОВАЯ аналитическая сессия: без неё сессии вернувшегося напрямую человека не
 * досталось бы ни одной атрибутирующей строки, и его продуктовые события
 * выпали бы из разрезов по кампании.
 */
export async function recordVisit(
  input: VisitInput,
  options: { force?: boolean } = {},
): Promise<AcquisitionState | null> {
  const visit = readVisit(input);
  const previous = await readState();
  const { state, persist, emit, isFirstTouch } = applyVisit(previous, visit, randomId());

  if (persist) await writeState(state);
  if (emit || options.force) {
    analytics.track(AcquisitionEvents.touch, touchProperties(state, isFirstTouch));
  }
  return state;
}

/**
 * Анонимная сессия стала пользователем.
 *
 * Событие авторизованное — `UserId` бэкенд проставит из JWT, а `sessionId`
 * тот же, что у `acquisition_touch`. Этой пары достаточно, чтобы связать
 * рекламное касание с аккаунтом, не копируя UTM ни в одно продуктовое событие.
 */
export async function trackIdentified(args: {
  method: IdentifyMethod;
  isNewUser: boolean;
}): Promise<void> {
  const state = await readState();
  analytics.track(AcquisitionEvents.identified, {
    method: args.method,
    isNewUser: args.isNewUser,
    ...(state
      ? {
        anonId: state.anonId,
        firstTouchAtUtc: state.first.touchAtUtc,
        firstSource: state.first.source,
        firstMedium: state.first.medium,
        firstCampaign: state.first.campaign,
        firstContent: state.first.content,
        source: state.last.source,
        medium: state.last.medium,
        campaign: state.last.campaign,
        content: state.last.content,
      }
      : {}),
  });
}
